import { pinyin } from 'pinyin-pro';

export type TableRename = {
  from: string;
  to: string;
};

export function renamePgTableConstants(source: string): { text: string; renames: TableRename[] } {
  const pgTableRegex = /export const\s+([^\s=]+)\s*=\s*(pgTable|pgView|pgMaterializedView)\(\s*["'`]([^"'`]+)["'`]/gu;
  const renames: TableRename[] = [];

  const updated = source.replace(pgTableRegex, (match, currentName: string, factory: string, tableName: string) => {
    const sanitized = sanitizeIdentifier(tableName);
    if (sanitized === currentName) {
      return match;
    }
    renames.push({ from: currentName, to: sanitized });
    const equalsIndex = match.indexOf('=');
    const suffix = equalsIndex >= 0 ? match.slice(equalsIndex) : ` = ${factory}("${tableName}"`;
    const normalizedSuffix = suffix.trimStart();
    return `export const ${sanitized} ${normalizedSuffix}`;
  });

  return { text: updated, renames };
}

export function updateTableReferenceIdentifiers(source: string, renames: TableRename[]): string {
  if (renames.length === 0) {
    return source;
  }

  return renames.reduce((acc, rename) => {
    if (!rename.from || rename.from === rename.to) {
      return acc;
    }
    const pattern = new RegExp(`\\b${escapeRegExp(rename.from)}(\\s*\\.)`, 'g');
    return acc.replace(pattern, `${rename.to}$1`);
  }, source);
}

export function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\//g, '\\/');
}

/**
 * Convert a string to camelCase
 * Example: "test_exam" -> "testExam", "user_profile_data" -> "userProfileData"
 */
function toCamelCase(str: string): string {
  // Split by underscores, hyphens, or spaces
  const words = str.split(/[_\-\s]+/).filter(Boolean);

  if (words.length === 0) {
    return '';
  }

  // First word lowercase, capitalize first letter of subsequent words
  return words
    .map((word, index) => {
      if (index === 0) {
        return word.toLowerCase();
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join('');
}

export function sanitizeIdentifier(name: string): string {
  const asciiName = toAsciiName(name);
  // Replace non-alphanumeric characters with underscores
  let sanitized = asciiName.replace(/[^A-Za-z0-9_]/g, '_');
  // Collapse multiple underscores into one
  sanitized = sanitized.replace(/_+/g, '_');
  // Remove leading/trailing underscores
  sanitized = sanitized.replace(/^_|_$/g, '');

  // Convert to camelCase
  sanitized = toCamelCase(sanitized);

  if (!sanitized) {
    sanitized = 'table';
  }
  if (!/^[A-Za-z_]/.test(sanitized)) {
    sanitized = `_${sanitized}`;
  }
  return sanitized;
}

export function toAsciiName(name: string): string {
  if (!/[^\x00-\x7F]/.test(name)) {
    return name;
  }

  try {
    const transliterated = pinyin(name, { toneType: 'none', type: 'array' }).join('_');
    return transliterated || name;
  } catch (error) {
    return name;
  }
}
